import { buildCollection } from "@firecms/core";
import {
    announcementTypes,
    type Announcement,
} from "@one-ai/shared/cms/schema";
import type { CollectionStrings } from "../i18n/collection-strings";

/**
 * The dropdown options are derived from `announcementTypes` in the shared
 * schema, so adding a category is one edit there plus a label in the site's
 * dictionaries — the two can never disagree about which keys are valid.
 *
 * Labels here are the editor-facing Chinese. The published label used to come
 * from `home.news.type.*` in `apps/onelive`'s dictionaries. That key went with
 * OneLive's announcements read on 2026-09-24, so no site renders it today.
 */
const typeLabels: Record<(typeof announcementTypes)[number], string> = {
    release: "開發公告",
};

/**
 * Typed with `Announcement` from the shared schema — the same module
 * `apps/onelive` parses reads through. A renamed field breaks this build.
 */
/**
 * **DEPRECATED 2026-09-03 — authoring only. The read path is untouched.**
 *
 * Both OneLive collections are retired from the CMS because neither has ever
 * held a record and neither is being authored. What that means precisely, since
 * "deprecated" could reasonably mean three different things here:
 *
 * - **Authoring is frozen.** `hideFromNavigation` takes it out of the drawer so
 *   an editor never meets it, and the permissions below refuse create, edit and
 *   delete. It stays *registered*, so a saved URL still resolves and the data
 *   remains inspectable — a soft delete, not a removal.
 * - **No site reads it any more.** `apps/onelive`'s home `SectionNews` read
 *   this collection until 2026-09-24, when it moved onto One AI's news stream
 *   filtered by `product` (`packages/shared/cms/product-news.ts`). The
 *   collection never held a record, so readers saw no change.
 * - **The schema and the rules stay.** `AnnouncementSchema` is imported by
 *   this file, and `firestore.rules` still grants public read. Removing the
 *   collection outright is now possible but is a separate decision.
 *
 * **To revive it:** delete `hideFromNavigation` and `permissions` below. The
 * localised labels are still maintained in `src/i18n/collections/*`, so there is
 * nothing else to restore.
 *
 * Reviving it also means giving it a reader again. Nothing renders this
 * collection since 2026-09-24.
 */
export const announcementsCollection = ({ announcements: t }: CollectionStrings) =>
    buildCollection<Announcement>({
        id: "announcements",
        path: "announcements",
        name: t.collection.name,
        singularName: t.collection.singularName,
        icon: "Campaign",
        description: t.collection.description,
        /**
         * Deprecated — see the note above. Out of the navigation drawer, and
         * frozen against writes rather than deleted.
         */
        hideFromNavigation: true,
        permissions: { read: true, create: false, edit: false, delete: false },
        properties: {
            date: {
                dataType: "string",
                name: t.fields.date.name,
                description: t.fields.date.description,
                validation: { required: true, matches: /^\d{4}-\d{2}-\d{2}$/ },
            },
            type: {
                dataType: "string",
                name: t.fields.type.name,
                validation: { required: true },
                enumValues: typeLabels,
                defaultValue: "release",
            },
            title: {
                dataType: "map",
                name: t.fields.title.name,
                expanded: true,
                properties: {
                    zh: {
                        dataType: "string",
                        name: "繁體中文",
                        validation: { required: true },
                    },
                    jp: {
                        dataType: "string",
                        name: "日本語",
                        validation: { required: true },
                    },
                },
            },
            url: {
                dataType: "string",
                name: t.fields.url.name,
                url: true,
                validation: { required: true },
            },
        },
    });
